import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, LogOut } from "lucide-react";
import { leaveAdminRecovery } from "../../api/recovery.js";
import { joinAdminRecovery } from "../../app/adminRecovery.js";
import { describeAuthError } from "../../app/authFlow.js";
import { useSession } from "../../app/session.js";
import { ME_KEY, useMe } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Alert, Badge, Button, ConfirmInline, Section } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { Fingerprint } from "./Fingerprint.js";

/** Settings: enrollment in admin-assisted recovery. Hidden while the instance has no recovery key. */
export function AdminRecoverySection(): ReactNode {
  const { state } = useSession();
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const status = me.data?.adminRecovery ?? null;
  if (status === null || me.data === undefined) {
    return null;
  }
  const required = me.data.policies.requireAdminRecovery;

  async function run(action: "join" | "leave"): Promise<void> {
    if (state.status !== "unlocked" || busy || status === null) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (action === "join") {
        await joinAdminRecovery(state.client, status);
      } else {
        await leaveAdminRecovery();
      }
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      toast.notify(action === "join" ? "Für die Admin-Wiederherstellung registriert." : "Admin-Wiederherstellung verlassen.", { tone: "success" });
      setConfirmLeave(false);
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      id="admin-recovery"
      title="Admin-Wiederherstellung"
      description="Vergisst du dein Master-Passwort und hast kein Notfall-Kit, kann ein Administrator deinen Zugang zurücksetzen. Dafür kann er auf deine Kontoschlüssel zugreifen."
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {status.enrolled ? <Badge tone="mint" icon={LifeBuoy}>Registriert</Badge> : <Badge tone="neutral">Nicht registriert</Badge>}
            {required ? <Badge tone="neutral">Vom Administrator verlangt</Badge> : null}
          </div>
          <p className="text-sm leading-relaxed text-mist">
            {status.enrolled && status.enrolledAt !== null ? (
              <>
                Registriert <span title={formatDateTime(status.enrolledAt)}>{formatRelativeTime(status.enrolledAt, Date.now())}</span>. Ein Zurücksetzen wird protokolliert, und du bekommst eine E-Mail.
              </>
            ) : required ? "Deine App registriert dein Konto beim nächsten Entsperren." : "Ohne Registrierung hilft bei einem vergessenen Master-Passwort nur dein Notfall-Kit."}
          </p>
          <p className="text-[0.8125rem] text-mist-2">
            Fingerabdruck des Instanz-Schlüssels: <Fingerprint publicKeyB64={status.publicKeyB64} />
          </p>
        </div>
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        {confirmLeave ? (
          <ConfirmInline
            message="Administratoren können deinen Zugang dann nicht mehr zurücksetzen."
            confirmLabel="Verlassen"
            pending={busy}
            onConfirm={(): void => void run("leave")}
            onCancel={(): void => setConfirmLeave(false)}
          />
        ) : status.enrolled ? (
          required ? null : (
            <div>
              <Button variant="ghost" icon={LogOut} onClick={(): void => setConfirmLeave(true)}>
                Verlassen
              </Button>
            </div>
          )
        ) : (
          <div>
            <Button variant="secondary" icon={LifeBuoy} loading={busy} onClick={(): void => void run("join")}>
              Registrieren
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
}
